// Design System Tokens - TypeScript interface for consistent color usage
// Extends the CSS custom properties defined in ds-tokens.css

export interface ColorTokens {
  // Status colors for trends and deltas
  positive: string;
  negative: string;
  neutral: string;
  
  // Chart colors
  axis: string;
  grid: string;
  series: {
    school: string;
    municipality: string;
    national: string;
    reference: string;
    goal: string;
  };
  
  // Background colors
  background: string;
  card: string;
  muted: string;
  
  // Text colors
  foreground: string;
  mutedForeground: string;
}

// Light theme tokens
export const lightTokens: ColorTokens = {
  positive: '#10b981', // emerald-500
  negative: '#ef4444', // red-500
  neutral: '#6b7280',  // slate-500
  
  axis: 'hsl(215 16% 47%)',
  grid: 'hsl(214 17% 92%)',
  series: {
    school: '#047857',     // emerald-700
    municipality: '#6b7280', // slate-500
    national: '#facc15',   // yellow-400
    reference: '#6b7280',  // slate-500
    goal: '#8b5cf6',       // violet-500
  },
  
  background: 'hsl(0 0% 100%)',
  card: 'hsl(0 0% 100%)',
  muted: 'hsl(210 40% 98%)',
  
  foreground: 'hsl(222.2 84% 4.9%)',
  mutedForeground: 'hsl(215 16% 47%)',
};

// Dark theme tokens
export const darkTokens: ColorTokens = {
  positive: '#10b981', // emerald-500
  negative: '#ef4444', // red-500
  neutral: '#6b7280',  // slate-500
  
  axis: 'hsl(215 20% 70%)',
  grid: 'hsl(217 19% 27%)',
  series: {
    school: '#10b981',     // emerald-500
    municipality: '#9ca3af', // gray-400
    national: '#fbbf24',   // amber-400
    reference: '#9ca3af',  // gray-400
    goal: '#a78bfa',       // violet-400
  },
  
  background: 'hsl(222.2 84% 4.9%)',
  card: 'hsl(222.2 84% 5.5%)',
  muted: 'hsl(222.2 47.4% 11.2%)',
  
  foreground: 'hsl(210 40% 98%)',
  mutedForeground: 'hsl(215 20% 65%)',
};

// Utility function to get current theme tokens
export const getTokens = (isDark: boolean = false): ColorTokens => {
  return isDark ? darkTokens : lightTokens;
};

// Tailwind CSS classes for consistent usage
export const tailwindClasses = {
  positive: {
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    text: 'text-emerald-700 dark:text-emerald-300',
    border: 'border-emerald-200 dark:border-emerald-800',
  },
  negative: {
    bg: 'bg-rose-50 dark:bg-rose-900/20',
    text: 'text-rose-700 dark:text-rose-300',
    border: 'border-rose-200 dark:border-rose-800',
  },
  neutral: {
    bg: 'bg-slate-50 dark:bg-slate-800',
    text: 'text-slate-700 dark:text-slate-300',
    border: 'border-slate-200 dark:border-slate-700',
  },
} as const;

// Chart theme configuration
export interface ChartTheme {
  axis: string;
  grid: string;
  series: {
    school: string;
    municipality: string;
    national: string;
    reference: string;
    goal: string;
  };
  tooltipBg: string;
  tooltipFg: string;
}

export const getChartTheme = (isDark: boolean = false): ChartTheme => {
  const tokens = getTokens(isDark);
  return {
    axis: tokens.axis,
    grid: tokens.grid,
    series: tokens.series,
    tooltipBg: isDark ? 'hsl(222.2 84% 5.5%)' : 'hsl(0 0% 100%)',
    tooltipFg: tokens.foreground,
  };
};
